import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, apiBaseUrl } from "@/services/apiClient";
import { applyVerifyResponse } from "@/services/authService";
import { enterpriseLogoUrl } from "@/services/platformEnterprisesService";

interface InviteInfo {
  email: string;
  enterpriseId: string;
  enterpriseName: string;
  productName: string;
  hasLogo: boolean;
  primaryColor: string | null;
  userExists: boolean;
}

const GOOGLE_ICON = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export function AcceptRequestInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const token = searchParams.get("token") ?? "";

  const [state, setState] = useState<"loading" | "ok" | "invalid">("loading");
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    apiClient.get<InviteInfo>(`/enterprise-invites/request-invite/verify`, { params: { token } })
      .then((res) => { setInvite(res.data); setState("ok"); })
      .catch(() => setState("invalid"));
  }, [token]);

  // Auto-accept when an authenticated user arrives (e.g. after login redirect)
  const autoAcceptedRef = useRef(false);
  useEffect(() => {
    if (!user || state !== "ok" || !invite || autoAcceptedRef.current) return;
    autoAcceptedRef.current = true;
    setAccepting(true);
    apiClient.post<{ success: boolean; requestId?: string }>(
      "/enterprise-invites/request-invite/accept", { token },
    ).then((res) => {
      const requestId = res.data.requestId;
      navigate(requestId ? `/requests/${requestId}?source=enterprise` : "/requests", { replace: true });
    }).catch(() => {
      setAccepting(false);
      autoAcceptedRef.current = false;
    });
  }, [user, state, invite, token, navigate]);

  async function handleAccept(): Promise<void> {
    setAccepting(true);
    setError(null);
    try {
      if (user) {
        const res = await apiClient.post<{ success: boolean; requestId?: string }>(
          "/enterprise-invites/request-invite/accept", { token },
        );
        const requestId = res.data.requestId;
        navigate(requestId ? `/requests/${requestId}?source=enterprise` : "/requests", { replace: true });
      } else {
        if (!firstName.trim() || !lastName.trim() || !password.trim()) {
          setError("Completá todos los campos");
          setAccepting(false);
          return;
        }
        if (password !== confirmPassword) {
          setError("Las contraseñas no coinciden");
          setAccepting(false);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = await apiClient.post<any>(
          "/enterprise-invites/request-invite/accept-with-registration",
          { token, firstName: firstName.trim(), lastName: lastName.trim(), password },
        );
        applyVerifyResponse(res.data);
        const requestId = res.data.requestId;
        // Full reload to pick up the new session in AuthContext
        window.location.href = requestId
          ? `/requests/${requestId}?source=enterprise`
          : "/requests";
        return;
      }
    } catch {
      setError("Error al aceptar la invitación.");
    } finally {
      setAccepting(false);
    }
  }

  function handleGoogleSso(): void {
    const returnTo = `/accept-request-invite?token=${encodeURIComponent(token)}`;
    setSsoLoading(true);
    window.location.href = `${apiBaseUrl}/auth/sso/google?returnTo=${encodeURIComponent(returnTo)}`;
  }

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === "invalid" || !invite) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Invitación inválida o expirada.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate("/login")}>
              Ir al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandColor = invite.primaryColor ?? "#0f172a";

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md overflow-hidden shadow-lg">
        {/* Branded header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ backgroundColor: brandColor }}>
          {invite.hasLogo ? (
            <img
              src={enterpriseLogoUrl(invite.enterpriseId)}
              alt={invite.enterpriseName}
              className="h-7 w-auto object-contain"
            />
          ) : (
            <span className="text-sm font-semibold text-white">
              {invite.enterpriseName}
            </span>
          )}
        </div>

        <CardContent className="px-6 pt-6 pb-6">
          {/* Heading */}
          <div className="space-y-2 mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              Invitación a participar
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Fuiste invitado a participar en una solicitud de{" "}
              <strong className="text-foreground">{invite.productName}</strong> con{" "}
              <strong className="text-foreground">{invite.enterpriseName}</strong>.
            </p>
          </div>

          {user ? (
            /* Authenticated state */
            <div className="space-y-5">
              <div className="rounded-lg border bg-muted/50 px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  Vas a aceptar como{" "}
                  <strong className="text-foreground">{user.email}</strong>
                </p>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full"
              >
                {accepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Aceptar invitación
              </Button>
            </div>
          ) : invite.userExists ? (
            /* Existing user — prompt to sign in */
            <div className="space-y-5">
              <div className="rounded-lg border bg-muted/50 px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  Ya tenés una cuenta con{" "}
                  <strong className="text-foreground">{invite.email}</strong>.
                  {" "}Inicia sesion para aceptar la invitacion.
                </p>
              </div>

              {/* Google SSO */}
              <button
                type="button"
                disabled={ssoLoading}
                onClick={handleGoogleSso}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border bg-background px-4 py-2.5 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
              >
                {ssoLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                ) : GOOGLE_ICON}
                Continuar con Google
              </button>

              <div className="relative text-center text-sm">
                <span className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </span>
                <span className="relative bg-card px-3 text-muted-foreground text-xs">
                  O inicia sesion con tu contraseña
                </span>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                className="w-full"
                onClick={() => {
                  const returnTo = `/accept-request-invite?token=${encodeURIComponent(token)}`;
                  navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
                }}
              >
                Iniciar sesion
              </Button>
            </div>
          ) : (
            /* New user — registration form */
            <div className="space-y-5">
              {/* Google SSO */}
              <button
                type="button"
                disabled={ssoLoading}
                onClick={handleGoogleSso}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border bg-background px-4 py-2.5 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
              >
                {ssoLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                ) : GOOGLE_ICON}
                Continuar con Google
              </button>

              {/* Divider */}
              <div className="relative text-center text-sm">
                <span className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </span>
                <span className="relative bg-card px-3 text-muted-foreground text-xs">
                  O crea una cuenta
                </span>
              </div>

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ri-first">Nombre</Label>
                  <Input
                    id="ri-first"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ri-last">Apellido</Label>
                  <Input
                    id="ri-last"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="ri-pass">Contraseña</Label>
                <PasswordInput
                  id="ri-pass"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordStrengthIndicator
                  password={password}
                  showRules
                  rulesLabels={{
                    minLength: t("auth.register.passwordStrength.minLength"),
                    uppercase: t("auth.register.passwordStrength.uppercase"),
                    number: t("auth.register.passwordStrength.number"),
                    special: t("auth.register.passwordStrength.special"),
                  }}
                  strengthLabels={{
                    weak: t("auth.register.passwordStrength.weak"),
                    fair: t("auth.register.passwordStrength.fair"),
                    good: t("auth.register.passwordStrength.good"),
                    strong: t("auth.register.passwordStrength.strong"),
                  }}
                />
              </div>

              {/* Confirm password */}
              <div className="space-y-1.5">
                <Label htmlFor="ri-confirm">Confirmar contraseña</Label>
                <PasswordInput
                  id="ri-confirm"
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-destructive">Las contraseñas no coinciden</p>
                )}
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="w-full"
              >
                {accepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Crear cuenta y aceptar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
